import * as React from 'react';
import {FormattedMessage, defineMessages} from 'react-intl';
import {Button, view, Link, Icon} from '@sail/ui';
import {download, external} from '@sail/icons/react/Icon';
import {
  InlineDatumReactComponentProps,
  useAnalytics,
} from '@stripe-internal/inline-data';
import {safeWindowOpen} from '@stripe-internal/safe-links';

type File = {
  name: string;
  tax_document_link?: string;
  express_dashboard_link?: string;
  document_page_url: string;
};

type ValueProps = {
  file: File;
};

const messages = defineMessages({
  downloadInstructionsStripe: {
    id: 'mse_inline_data.tax1099.download.instructionsStripe',
    description: 'Instructions for downloading 1099-K form for Stripe users',
    defaultMessage:
      'Got it. You can download your 1099-K form below or via the {documentsLink} page on the Stripe dashboard.',
  },
  downloadInstructionsExpress: {
    id: 'mse_inline_data.tax1099.download.instructionsExpress',
    description: 'Instructions for downloading 1099 form for Express users',
    defaultMessage:
      "Got it. You can download your 1099 form via the Tax forms page on the Express dashboard. You'll be asked to provide the last four digits of your TIN for security purposes.",
  },
  documentsLinkText: {
    id: 'mse_inline_data.tax1099.download.documentsLinkText',
    description: 'Text for the link to the Documents page',
    defaultMessage: 'Documents',
  },
  downloadButton: {
    id: 'mse_inline_data.tax1099.download.downloadButton',
    description: 'Text for Download button',
    defaultMessage: 'Download',
  },
  expressDashboardButton: {
    id: 'mse_inline_data.tax1099.download.expressDashboardButton',
    description: 'Text for Express Dashboard button',
    defaultMessage: 'Express dashboard',
  },
});

export const TaxForm1099CurrentYear = (
  props: InlineDatumReactComponentProps,
) => {
  const {datum} = props;
  const value = datum.value as ValueProps;
  const {file} = value;
  const analytics = useAnalytics();

  const handleButtonClick = () => {
    if (file.tax_document_link) {
      const link = document.createElement('a');
      link.href = file.tax_document_link;
      link.download = `${file.name}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      analytics.action('uh_stripe_tax_form_download');
    } else if (file.express_dashboard_link) {
      safeWindowOpen(file.express_dashboard_link);
      analytics.action('uh_express_dashboard_link_open');
    }
  };

  const isExpressUser = !!file.express_dashboard_link;

  return (
    <view.div>
      <view.div
        css={{
          marginBottom: 'small',
        }}
      >
        <FormattedMessage
          {...(isExpressUser
            ? messages.downloadInstructionsExpress
            : messages.downloadInstructionsStripe)}
          values={{
            documentsLink: (
              <Link
                type="primary"
                href={file.document_page_url}
                target="_blank"
                rel="noreferrer"
              >
                <FormattedMessage {...messages.documentsLinkText} />
              </Link>
            ),
          }}
        />
      </view.div>

      <Button
        type="primary"
        css={{width: 'fill', marginBottom: 'small'}}
        onClick={handleButtonClick}
      >
        {isExpressUser ? (
          <>
            <FormattedMessage {...messages.expressDashboardButton} />
            <Icon icon={external} />
          </>
        ) : (
          <>
            <Icon icon={download} />
            {file.name}
          </>
        )}
      </Button>
    </view.div>
  );
};
